// components/Navbar.js (Redesigned)
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faBars, 
  faTimes, 
  faHammer,
  faPhone,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { Link, useLocation } from 'react-router-dom';

import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Our Work', href: '/projects' },
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'py-3 bg-white/95 backdrop-blur-xl shadow-2xl' : 'py-5 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
            <div className="flex items-center">
              <div className="relative">
                {/* Logo Image */}
                <img 
                  src={logo} 
                  alt="KA Construction Solutions LTD" 
                  className={`h-12 w-auto ${scrolled ? 'filter brightness-100' : 'filter brightness-0 invert'}`}
                />
              
              </div>
              <div className="ml-3">
                <span className={`text-2xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                  KA Construction 
                </span>
                <div className={`text-sm ${scrolled ? 'text-gray-600' : 'text-gray-300'}`}>Solutions LTD</div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                <Link
                  to={link.href}
                  className={`flex items-center font-medium transition-all duration-300 hover:text-yellow-600 text-lg ${scrolled ? 'text-gray-700' : 'text-white'} ${
                    isActive(link.href) ? 'text-yellow-600' : ''
                  }`}
                >
                  <span className="relative pb-1">
                    {link.name}
                    {/* Active Border Indicator */}
                    {isActive(link.href) && (
                      <div className="absolute -bottom-1 left-0 right-0 h-1 bg-yellow-600 rounded-full"></div>
                    )}
                  </span>
                  {link.dropdown && (
                    <FontAwesomeIcon icon={faChevronDown} className="ml-1 h-3 w-3" />
                  )}
                </Link>
                
                {/* Hover Border Indicator */}
                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            ))}
            
            {/* Contact Button */}
            <div className="flex items-center space-x-4">
              <Link to='/contact'>
                <button className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 overflow-hidden text-lg ${scrolled ? 'bg-yellow-600 text-white hover:bg-yellow-700' : 'bg-white text-gray-900 hover:bg-gray-100'}`}>
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative">Get Quote</span>
                </button>
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon 
              icon={isOpen ? faTimes : faBars} 
              className={`h-7 w-7 ${scrolled ? 'text-gray-700' : 'text-white'}`}
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 ${isOpen ? 'max-h-screen opacity-100 mt-4' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/20">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block px-4 py-4 text-lg font-medium transition-colors rounded-lg relative ${
                    isActive(link.href) 
                      ? 'text-yellow-600 bg-yellow-50 pl-6' 
                      : 'text-gray-700 hover:text-yellow-600 hover:bg-yellow-50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {isActive(link.href) && (
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-yellow-600 rounded-full"></div>
                  )}
                  {link.name}
                  {isActive(link.href) && (
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-yellow-600 rounded-r-full"></div>
                  )}
                </Link>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center text-gray-700 mb-4">
                <FontAwesomeIcon icon={faPhone} className="h-5 w-5 mr-3 text-yellow-600" />
                <div>
                  <div className="font-semibold text-lg">   
                    <a href="tel:01912508966" className="hover:text-yellow-600 transition-colors">+44 0191 250 8966</a>
                  </div>
                </div>
              </div>
              <Link to='/contact'>
                <button className="w-full bg-gradient-to-r from-yellow-600 to-yellow-600 text-white py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300">
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Request Quote
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;